<template>
  <img :key="attempt.key" :src="attempt.src" :alt="alt" @load="attempt.onLoad" @error="attempt.onError" />
</template>

<script>
import { onBeforeUnmount, shallowRef, watch } from 'vue'
import { GetBoqItemImageContent } from '@/actions/boqImageActions.js'

import { ShouldUseBoqImageProxy, MarkBoqImageCdnSlow, MarkBoqImageCdnHealthy } from '@/helpers/boqImageDelivery.js'

const CDN_LOAD_TIMEOUT = 2500
const IMAGE_LOAD_TIMEOUT = 8000
const noop = () => {}

export default {
  name: 'BoqItemImage',
  props: {
    boqId: { type: String, required: true },
    itemId: { type: String, required: true },
    imageContextType: { type: String, default: 'boq' },
    image: { type: Object, required: true },
    alt: { type: String, default: '' },
    resolvedSrc: { type: String, default: '' }
  },
  emits: ['load', 'error'],
  setup(props, { emit }) {
    const attempt = shallowRef({ key: 0, src: undefined, onLoad: noop, onError: noop })
    let sequence = 0
    let active = null

    const clearTimer = context => {
      if (context.timer !== null) clearTimeout(context.timer)
      context.timer = null
    }
    const revokeBlob = context => {
      if (context.blobUrl) URL.revokeObjectURL(context.blobUrl)
      context.blobUrl = null
    }
    const cleanup = context => {
      if (!context) return
      context.phase = 'disposed'
      clearTimer(context)
      context.controller?.abort()
      revokeBlob(context)
    }
    const current = (context, phase) => active === context && context.phase === phase
    const fail = (context, reason, status) => {
      if (active !== context || ['disposed', 'loaded', 'failed'].includes(context.phase)) return
      context.phase = 'failed'
      clearTimer(context)
      revokeBlob(context)
      const detail = { url: context.url, reason }
      if (Number.isInteger(status)) detail.status = status
      emit('error', detail)
    }
    const load = (context, phase, displayUrl) => {
      if (!current(context, phase)) return
      context.phase = 'loaded'
      if (phase === 'cdn') MarkBoqImageCdnHealthy(context.url)
      clearTimer(context)
      emit('load', { url: context.url, displayUrl })
    }
    const showAttempt = (src, onLoad = noop, onError = noop) => {
      // Each attempt owns its handlers; events from a removed image cannot
      // change the result for a newer URL or a different BOQ line item.
      attempt.value = { key: ++sequence, src, onLoad, onError }
    }
    const fallback = async (context, phase = 'cdn') => {
      if (!current(context, phase)) return
      clearTimer(context)
      context.phase = 'fetching'
      showAttempt(undefined)
      if (!context.boqId || !context.itemId || !context.imageId) {
        fail(context, 'missing_context')
        return
      }
      context.controller = new AbortController()
      try {
        const blob = await GetBoqItemImageContent(context.boqId, context.itemId, context.imageId, {
          signal: context.controller.signal,
          imageContextType: context.imageContextType
        })
        if (!current(context, 'fetching')) return
        if (!(blob instanceof Blob) || !blob.size) {
          fail(context, 'invalid_image_content')
          return
        }
        context.blobUrl = URL.createObjectURL(blob)
        context.phase = 'blob'
        showAttempt(context.blobUrl,
          () => load(context, 'blob', context.blobUrl),
          () => { if (current(context, 'blob')) fail(context, 'image_decode_failed') })
        context.timer = setTimeout(() => {
          if (current(context, 'blob')) fail(context, 'image_decode_timeout')
        }, IMAGE_LOAD_TIMEOUT)
      } catch (error) {
        if (current(context, 'fetching')) fail(context, 'image_content_failed', error.response?.status)
      }
    }

    watch([() => props.boqId, () => props.itemId, () => props.image?._id, () => props.image?.url, () => props.imageContextType, () => props.resolvedSrc],
      ([boqId, itemId, imageId, value, imageContextType, resolvedSrc]) => {
        cleanup(active)
        const context = { boqId, itemId, imageId, imageContextType, url: typeof value === 'string' ? value : '',
          phase: 'cdn', timer: null, controller: null, blobUrl: null }
        active = context
        let validUrl = false
        try {
          const base = typeof window === 'undefined' ? 'http://localhost/' : window.location.href
          const parsed = new URL(context.url, base)
          validUrl = Boolean(context.url.trim()) && ['http:', 'https:'].includes(parsed.protocol)
        } catch { /* Invalid sources must never become an empty-src request. */ }
        if (!validUrl) {
          showAttempt(undefined)
          Promise.resolve().then(() => { if (current(context, 'cdn')) fail(context, 'invalid_image_url') })
          return
        }
        if (resolvedSrc && (resolvedSrc.startsWith('blob:') || /^https?:\/\//i.test(resolvedSrc))) {
          context.phase = 'resolved'
          showAttempt(resolvedSrc, () => load(context, 'resolved', resolvedSrc), () => fallback(context, 'resolved'))
          context.timer = setTimeout(() => fallback(context, 'resolved'), IMAGE_LOAD_TIMEOUT)
          return
        }
        if (ShouldUseBoqImageProxy(context.url)) {
          showAttempt(undefined)
          fallback(context)
          return
        }
        showAttempt(context.url, () => load(context, 'cdn', context.url), () => fallback(context))
        context.timer = setTimeout(() => {
          if (!current(context, 'cdn')) return
          MarkBoqImageCdnSlow(context.url)
          fallback(context)
        }, CDN_LOAD_TIMEOUT)
      }, { immediate: true })

    onBeforeUnmount(() => {
      cleanup(active)
      active = null
    })
    return { attempt }
  }
}
</script>
